import { AosSeverity } from 'aos-services/src/services/common/types/AosSeverity'
import {
    defaultTaskRecurrence,
    TaskRecurrencePayload,
} from 'aos-services/src/services/tasks/types/payload/TaskRecurrenceFormPayload'
import { defaultTaskRecurrenceHourly } from 'aos-services/src/services/tasks/types/payload/TaskRecurrenceHourlyFormPayload'
import {
    allTaskRecurrenceTypes,
    translateTaskRecurrenceType,
} from 'aos-services/src/services/tasks/types/task/TaskRecurrence'
import { TaskRecurrenceType } from 'aos-services/src/services/tasks/types/task/TaskRecurrenceType'
import {
    Box,
    Dropdown,
    DurationPicker,
    HStack,
    Switch,
    Text,
    Tooltip,
    TooltipContent,
    TooltipText,
    VStack,
} from 'aos-ui-universal'
import React, { FC } from 'react'
import { FieldErrors, useController, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TaskFormField } from '../../TaskFormField'
import { TaskRecurrenceFormProps } from './TaskRecurrenceFormProps'
import { TaskRecurrenceHourly } from './TaskRecurrenceHourly'
import { TaskRecurrenceMonthly } from './TaskRecurrenceMonthly'
import { TaskRecurrenceWeekly } from './TaskRecurrenceWeekly'
import { TaskRecurrenceYearly } from './TaskRecurrenceYearly'

interface TaskRecurrenceProps {
    recurrenceChangeable: boolean
    disabled?: boolean
}

export const TaskRecurrence: FC<TaskRecurrenceProps> = ({ recurrenceChangeable, disabled }) => {
    const { t: translate } = useTranslation()
    const { control } = useFormContext()
    const recurrenceField = useController({ name: 'recurrence', control })
    const recurrenceScheduleField = useController({ name: 'recurrenceSchedule', control })
    const task = useWatch({ control }) as TaskRecurrenceTask
    const recurrenceErrors = recurrenceField.fieldState.error as
        | FieldErrors<TaskRecurrencePayload>
        | undefined
    const recurrenceScheduleError = !!recurrenceScheduleField.fieldState.error
    const toggle = (enabled: boolean) => {
        recurrenceField.field.onChange(enabled ? defaultTaskRecurrenceHourly : undefined)
    }

    const changeType = (type: TaskRecurrenceType | null) => {
        if (type) {
            recurrenceField.field.onChange(defaultTaskRecurrence[type])
        }
    }

    const updateRecurrence = (value: Partial<TaskRecurrencePayload>) => {
        const recurrence = { ...task.recurrence, ...value } as TaskRecurrencePayload
        recurrenceField.field.onChange(recurrence)
    }

    const RecurrenceTypeFromComponent = task.recurrence && recurrenceTypeFrom[task.recurrence.type]

    return (
        <VStack className='shrink-0 gap-4'>
            <HStack className='shrink-0 items-center justify-between'>
                <Text size='body' tone='default' weight='medium'>
                    {translate('recurrence', { ns: 'tasks' })}
                </Text>

                {task.severity === AosSeverity.Emergency ? (
                    <Tooltip
                        placement='top'
                        trigger={triggerProps => (
                            <Box {...triggerProps}>
                                <Switch value={!!task.recurrence} isDisabled />
                            </Box>
                        )}
                    >
                        <TooltipContent>
                            <TooltipText>
                                {translate('recurrence.disabled', { ns: 'tasks' })}
                            </TooltipText>
                        </TooltipContent>
                    </Tooltip>
                ) : (
                    <Switch
                        value={!!task.recurrence}
                        onValueChange={toggle}
                        isDisabled={!recurrenceChangeable || disabled}
                    />
                )}
            </HStack>

            {task.recurrence && (
                <TaskFormField
                    label={translate('repeats', { ns: 'tasks' })}
                    error={
                        recurrenceScheduleError
                            ? translate('recurrence.error', { ns: 'tasks' })
                            : undefined
                    }
                >
                    <Dropdown
                        value={task.recurrence.type}
                        items={allTaskRecurrenceTypes}
                        labelRenderer={translateTaskRecurrenceType(translate)}
                        onChange={changeType}
                        hasClear={false}
                    />
                </TaskFormField>
            )}

            {RecurrenceTypeFromComponent && (
                <RecurrenceTypeFromComponent
                    value={task.recurrence!}
                    onChange={updateRecurrence}
                    errors={recurrenceErrors}
                    canEdit
                />
            )}

            {task.recurrence && (
                <TaskFormField label={translate('duration', { ns: 'tasks' })}>
                    <DurationPicker
                        minutes={task.recurrence.duration}
                        onChange={duration => updateRecurrence({ duration })}
                        className='w-full'
                    />
                </TaskFormField>
            )}
        </VStack>
    )
}

const recurrenceTypeFrom: Record<TaskRecurrenceType, FC<TaskRecurrenceFormProps>> = {
    [TaskRecurrenceType.Hourly]: TaskRecurrenceHourly,
    [TaskRecurrenceType.Weekly]: TaskRecurrenceWeekly,
    [TaskRecurrenceType.Monthly]: TaskRecurrenceMonthly,
    [TaskRecurrenceType.Yearly]: TaskRecurrenceYearly,
}

type TaskRecurrenceTask = {
    recurrence?: TaskRecurrencePayload
    severity?: AosSeverity
}
